import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowUpRight, MapPin } from "lucide-react";
import { PageHero, Overline, PrimaryButton } from "@/components/common";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import { PORTFOLIO, PROJECTS, IMAGES } from "@/data/site";

const EASE = [0.16, 1, 0.3, 1];

const Gallery = ({ title, images }) => (
  <div>
    <p className="font-display text-xs uppercase tracking-[0.2em] text-slate-400">{title}</p>
    <div className="mt-3 grid grid-cols-2 gap-2">
      {images.map((img, i) => (
        <figure key={i} className="group relative aspect-[4/3] overflow-hidden bg-slate-100">
          <img src={img.src} alt={img.caption} className="h-full w-full object-cover" loading="lazy" />
          <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#0A1128]/90 to-transparent px-3 pb-2 pt-8 font-display text-[11px] uppercase tracking-wide text-white">
            {img.caption}
          </figcaption>
        </figure>
      ))}
    </div>
  </div>
);

export default function Portfolio() {
  const [activeId, setActiveId] = useState(null);
  const active = activeId ? PROJECTS[activeId] : null;

  useEffect(() => {
    document.body.style.overflow = active ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [active]);

  return (
    <>
      <PageHero
        overline="Project Portfolio"
        title="Work that speaks for itself"
        intro="Real industrial and commercial projects — over-cladding, roof replacement and full unit refurbishment across Milton Keynes and the wider region, with genuine before-and-after results."
        image={IMAGES.windsorAfter2}
      />

      <section className="container-x py-16 md:py-24">
        <div className="grid auto-rows-[240px] grid-cols-1 gap-px bg-slate-200 sm:grid-cols-2 md:auto-rows-[280px] lg:grid-cols-4">
          {PORTFOLIO.map((tile, i) => (
            <motion.button
              key={`${tile.projectId}-${i}`}
              onClick={() => setActiveId(tile.projectId)}
              data-testid={`portfolio-card-${tile.projectId}-${i}`}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true, margin: "-4%" }}
              transition={{ duration: 0.6, ease: EASE, delay: (i % 4) * 0.05 }}
              className={`view-cursor group relative overflow-hidden bg-[#0A1128] text-left ${tile.span}`}
            >
              <div className="clip-frame h-full w-full">
                <img src={tile.image} alt={`${tile.label} — ${tile.region}`} className="h-full w-full object-cover opacity-85 transition-opacity group-hover:opacity-60" loading="lazy" />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1128] via-[#0A1128]/10 to-transparent" />
              <div className="absolute inset-0 flex flex-col justify-end p-6">
                <span className="font-display text-[10px] uppercase tracking-[0.2em] text-[#4b6bd6]">{tile.label}</span>
                <h3 className="mt-1 font-display text-lg font-medium uppercase leading-tight tracking-tight text-white md:text-xl">
                  {tile.region}
                </h3>
                <span className="mt-2 inline-flex items-center gap-1 text-xs text-slate-300 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                  View project <ArrowUpRight size={14} />
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      </section>

      <section className="border-t border-slate-200 py-20 text-center">
        <div className="container-x flex flex-col items-center">
          <Overline>Have a project in mind?</Overline>
          <h2 className="mt-5 max-w-3xl font-display text-3xl font-bold uppercase tracking-tight text-[#0A1128] md:text-5xl">
            Let's discuss what you need
          </h2>
          <div className="mt-10">
            <PrimaryButton to="/contact" testId="portfolio-contact-btn">
              Request a Call Back
            </PrimaryButton>
          </div>
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-0 backdrop-blur-sm md:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setActiveId(null)}
            data-testid="portfolio-modal"
          >
            <motion.div
              onClick={(e) => e.stopPropagation()}
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.5, ease: EASE }}
              data-lenis-prevent
              className="relative flex h-full w-full max-w-3xl flex-col overflow-y-auto overscroll-contain bg-white md:h-auto md:max-h-[90vh]"
            >
              <button
                onClick={() => setActiveId(null)}
                aria-label="Close project"
                data-testid="portfolio-modal-close"
                className="absolute right-4 top-4 z-20 bg-[#0A1128] p-2 text-white transition-colors hover:bg-[#1D4ED8]"
              >
                <X size={22} />
              </button>

              {active.compare ? (
                <div className="shrink-0 bg-[#0A1128] px-5 pb-5 pt-14 md:px-8 md:pt-8">
                  <p className="mb-3 font-display text-xs uppercase tracking-[0.2em] text-[#4b6bd6]">
                    Before &amp; After — drag the handle to compare
                  </p>
                  <BeforeAfterSlider before={active.compare.before} after={active.compare.after} dark />
                </div>
              ) : (
                <div className="aspect-[16/9] w-full shrink-0 overflow-hidden">
                  <img src={active.after?.[0]?.src || active.before?.[0]?.src} alt={active.title} className="h-full w-full object-cover" />
                </div>
              )}

              <div className="p-8 md:p-12">
                <span className="font-display text-xs uppercase tracking-[0.2em] text-[#1D4ED8]">{active.type}</span>
                <h3 className="mt-3 font-display text-3xl font-bold uppercase leading-none tracking-tight text-[#0A1128] md:text-4xl">
                  {active.title}
                </h3>
                <p className="mt-4 inline-flex items-center gap-2 text-sm text-slate-500">
                  <MapPin size={15} /> {active.region}
                </p>

                <div className="mt-8 space-y-6">
                  <div>
                    <p className="font-display text-xs uppercase tracking-[0.2em] text-slate-400">Scope</p>
                    <p className="mt-2 text-sm leading-relaxed text-slate-700">{active.scope}</p>
                  </div>
                  <div>
                    <p className="font-display text-xs uppercase tracking-[0.2em] text-slate-400">Challenges</p>
                    <p className="mt-2 text-sm leading-relaxed text-slate-700">{active.challenge}</p>
                  </div>
                  <div>
                    <p className="font-display text-xs uppercase tracking-[0.2em] text-slate-400">Outcome</p>
                    <p className="mt-2 text-sm leading-relaxed text-slate-700">{active.outcome}</p>
                  </div>
                </div>

                <div className="mt-10 space-y-8">
                  {active.before && <Gallery title={active.compare ? "All before shots" : "Before"} images={active.before} />}
                  {active.after && <Gallery title={active.before ? (active.compare ? "All after shots" : "After") : "Project gallery"} images={active.after} />}
                </div>

                <div className="mt-10">
                  <PrimaryButton to="/contact" testId="portfolio-modal-contact">
                    Enquire About Similar Work
                  </PrimaryButton>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
